import React, { useMemo } from "react";
import { useAppTranslation } from "../../../../i18n/useAppTranslation";
import { PROCEDURE_STATE_TYPES } from "../../../../consts/general";
import {
  removeNullishFromShape,
  unwrapOr,
} from "../../../../utilities/general";
import { CompleteIcon } from "../../components/CompleteIcon/CompleteIcon";
import { ProcedureTableInfo } from "../../components/ProcedureTableInfo/ProcedureTableInfo";
import { CompleteLink } from "../../components/CompleteLink/CompleteLink";
import { useCurrentUserQuery } from "../../../../api/queries/useUserQuery";
import { ProcedureTreatmentSummary } from "../../components/ProcedureTreatmentSummary/ProcedureTreatmentSummary";
import TabCosmeticRowActions from "../components/TabCosmeticRowActions";
import {
  getProcedureRoutes,
  getProcedureStatuses,
} from "../../ProviderRoom.utils";
import parentClasses from "../../sass/ProviderRoom.module.scss";
import { Checkbox } from "../../../../shared/Checkbox/Checkbox";
import { useRoomStoreMultiselect } from "../../hooks/useRoomStoreMultiselect";

const type = PROCEDURE_STATE_TYPES.signedProcedures;

export function useTableCosmetic({
  procedures = [],
  showHideActionButton,
  providerUserId,
  signList,
}) {
  const { tCommon } = useAppTranslation.Common();
  const { data: user } = useCurrentUserQuery();

  const filteredProcedureIds = procedures
    .filter((p) => !p.isSentToMd)
    .map((p) => p.id);

  const { isSelected, isSelectedAll, onSelectAll, onSelectOne, resetSelected } =
    useRoomStoreMultiselect({
      allEntities: filteredProcedureIds,
      roomSignList: signList,
    });

  const isActionsAvailable = unwrapOr(
    () => Boolean(user.isMdConsentRequired),
    false,
  );

  const isScriptsureEnabled = unwrapOr(
    () => Boolean(user.account.integrations.isScriptsureEnabled),
    false,
  );

  const cols = [
    {
      data: (
        <Checkbox
          inverted
          size="small"
          isChecked={isSelectedAll()}
          onChange={onSelectAll}
        />
      ),
      accessor: "selectCheck",
    },
    {
      data: tCommon("providerRoom.table.procedureInfo"),
      accessor: "info",
    },
    {
      data: tCommon("label.patient"),
      accessor: "patientName",
    },
    {
      data: tCommon("providerRoom.table.clinic"),
      accessor: "clinicName",
    },
    {
      data: tCommon("providerRoom.table.treatmentSummary"),
      accessor: "treatmentSummary",
    },
    {
      data: tCommon("providerRoom.table.latestNote"),
      accessor: "hasLatestNote",
      headColClassName: parentClasses.tableHeadCentered,
    },
    {
      data: tCommon("providerRoom.table.consent"),
      accessor: "hasConsent",
      headColClassName: parentClasses.tableHeadCentered,
    },
    {
      data: tCommon("providerRoom.table.questionnaire"),
      accessor: "hasQuestionnaire",
      headColClassName: parentClasses.tableHeadCentered,
    },
    ...(isScriptsureEnabled
      ? [
          {
            data: tCommon("providerRoom.table.prescription"),
            accessor: "hasPrescription",
            headColClassName: parentClasses.tableHeadCentered,
          },
        ]
      : []),
    {
      data: tCommon("providerRoom.table.consultOnly"),
      accessor: "consultOnly",
      headColClassName: parentClasses.tableHeadCentered,
    },
    ...(isActionsAvailable && !showHideActionButton
      ? [
          {
            data: tCommon("providerRoom.table.actions"),
            accessor: "actions",
          },
        ]
      : []),
  ];

  const data = useMemo(
    () =>
      procedures.map((p) => {
        const { hasNotes, hasConsent, hasQuestionnaire, hasPrescription } =
          getProcedureStatuses(p);

        const {
          infoRoute,
          notesRoute,
          consentRoute,
          questionnaireRoute,
          prescriptionRoute,
          viewQuestionnaireRoute,
          viewConsentRoute,
        } = getProcedureRoutes({
          procedure: p,
          type,
        }).cosmetic;

        const isCompleted = hasNotes && hasConsent && hasQuestionnaire;
        const routteWihState = {
          pathname: infoRoute,
          state: {
            chartUserId: providerUserId,
            isFromChartAuditReport: showHideActionButton,
          },
        };

        return removeNullishFromShape({
          key: p.id,

          selectCheck: !p.isSentToMd ? (
            <Checkbox
              size="small"
              isChecked={isSelected(p.id)}
              onChange={() => onSelectOne(p.id)}
              isDisabled={showHideActionButton}
            />
          ) : (
            ""
          ),

          info: (
            <ProcedureTableInfo
              title={p.name}
              rawDate={p.date}
              link={
                showHideActionButton || providerUserId
                  ? routteWihState
                  : infoRoute
              }
            />
          ),

          patientName: unwrapOr(
            () => p.patient.fullName,
            tCommon("symbol.longDash"),
          ),

          clinicName: unwrapOr(
            () => p.clinic.clinic_name,
            tCommon("symbol.longDash"),
          ),

          treatmentSummary: (
            <ProcedureTreatmentSummary
              injectionsList={p.injectionList}
              info={p.info}
            />
          ),

          hasLatestNote: (
            <CompleteLink
              isCompleted={hasNotes}
              to={notesRoute}
              isDisabled={showHideActionButton}
            >
              <CompleteIcon isCompleted={hasNotes} />
            </CompleteLink>
          ),

          hasConsent: (
            <CompleteLink
              isCompleted={hasConsent}
              to={hasConsent ? consentRoute : viewConsentRoute}
              isDisabled={showHideActionButton}
            >
              <CompleteIcon isCompleted={hasConsent} />
            </CompleteLink>
          ),

          hasQuestionnaire: (
            <CompleteLink
              isCompleted={hasQuestionnaire}
              to={
                hasQuestionnaire ? questionnaireRoute : viewQuestionnaireRoute
              }
              isDisabled={showHideActionButton}
            >
              <CompleteIcon isCompleted={hasQuestionnaire} />
            </CompleteLink>
          ),

          hasPrescription: hasPrescription ? (
            <CompleteLink
              isCompleted={hasPrescription}
              to={prescriptionRoute}
              isDisabled={showHideActionButton}
            >
              <CompleteIcon isCompleted={hasPrescription} />
            </CompleteLink>
          ) : (
            <CompleteIcon isCompleted={hasPrescription} />
          ),

          consultOnly: (
            <div className={parentClasses.tableHeadCentered}>
              {p.isConsult ? tCommon("label.yes") : tCommon("label.no")}
            </div>
          ),

          actions: isActionsAvailable ? (
            <TabCosmeticRowActions
              procedureId={p.id}
              isSentToMd={p.isSentToMd}
              isComplete={isCompleted}
            />
          ) : undefined,
        });
      }),
    [procedures, user, signList],
  );

  return {
    cols,
    data,
    resetSelected,
  };
}
